import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { carteraApi } from './api';
import { money, money0, iniciales, avaColor, ETAPA_LABELS, ETAPA_COLORS } from './helpers';
import { CountUp, AnimatedDonut, MultiSeriesChart, ScatterChart, HBars } from './CarteraWidgets';
import DrillPopover from './DrillPopover';
import peicarMark from '../../../Assets/Images/peicar-abstract-vector.svg';

/* Buckets de aging del endpoint -> filas del semáforo. */
const AGING_DEFS = [
  { key: 'al_corriente', label: 'Al corriente', rango: 'sin atraso', color: '#00BA6E' },
  { key: '1_30', label: 'Vencida', rango: '1–30 días', color: '#F0961E' },
  { key: '31_90', label: 'Administrativa', rango: '31–90 días', color: '#E5631B' },
  { key: '91_180', label: 'Extrajudicial', rango: '91–180 días', color: '#DC2626' },
  { key: 'mas_180', label: 'Crítica / castigo', rango: '+180 días', color: '#991B1B' },
];

const ESTATUS_DOT = {
  vigente: '#00BA6E', vencido: '#F0961E', deteriorado: '#DC2626',
  liquidado: '#64748B', castigado: '#991B1B',
};

/* Identidad de color por producto (mismos del dashboard del negocio). Hex
 * directo porque van a atributos SVG (stroke), donde var() no aplica. */
const PROD_COLORS = { AP: '#34C6F1', AF: '#00BA6E', CS: '#21588C' };
const PROD_NAMES = { AP: 'AP Puro', AF: 'AF Financiero', CS: 'CS Simple' };

/* Series de la evolución 12m (el orden define el apilado, abajo→arriba). */
const S12M_SERIES = [
  { key: 'ap', label: 'AP rentas por cobrar', color: PROD_COLORS.AP },
  { key: 'af', label: 'AF capital', color: PROD_COLORS.AF },
  { key: 'cs', label: 'CS saldo', color: PROD_COLORS.CS },
];

const BUCKET_COLORS = ['#F5C518', '#F5A623', '#EA7A1F', '#E4572E', '#DC2626', '#B91C1C', '#8F1D1D', '#5C0F14'];
const HISTORIC_EMPTY = 'sin dato histórico — las fotos empiezan en jul-2026';

/* $25.16M para el strip (moneyShort redondea a 1 decimal; aquí van 2). */
const moneyM = (v) => (Math.abs(Number(v) || 0) >= 1e6
  ? '$' + (Number(v) / 1e6).toFixed(2) + 'M'
  : money0(v));

const hasNumber = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));

const monthLabelLong = (ym) => {
  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) {
    return new Date().toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
  }
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1, 12).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
};

const captureLabel = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
};

const timelineTypeLabel = (tipo) => {
  if (tipo === 'real') return 'foto del cierre';
  if (tipo === 'simulado') return 'simulado';
  return 'actual';
};

const deriveBandejaFromResumen = (rows = []) => rows
  .filter((c) => c.estatus === 'vencido' || c.estatus === 'deteriorado')
  .slice()
  .sort((a, b) => (Number(b.dias_vencido) || 0) - (Number(a.dias_vencido) || 0));

/* '2026-07-15' -> 'mar 15 jul' (mediodía local para esquivar el corrimiento UTC). */
const fmtFecha = (iso) => {
  if (!iso) return '—';
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });
};

function HistoricalEmpty({ icon = 'pi pi-history', title = HISTORIC_EMPTY }) {
  return (
    <div className="empty-state historic-empty">
      <i className={icon} />
      <div className="es-title">{title}</div>
      <div className="es-sub">La foto congelada de cobranza aún no existe para este mes.</div>
    </div>
  );
}

function TimelineBar({ items, activeMes, loading, onSelect, onPrev, onNext, onToday }) {
  if (!items.length) return null;
  const activeIndex = Math.max(0, items.findIndex((x) => x.mes === activeMes));
  const active = items[activeIndex] || items[items.length - 1];
  const pct = items.length > 1 ? (activeIndex / (items.length - 1)) * 100 : 100;

  const onRailClick = (e) => {
    if (loading || items.length < 2) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const idx = Math.round(ratio * (items.length - 1));
    const m = items[idx];
    if (m && m.mes !== activeMes) onSelect(m.mes);
  };

  return (
    <motion.div className="timeline-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
      <span className="tl-label">Línea de tiempo</span>
      <div className="tl-active-wrap">
        <span className="tl-active">{active?.label || active?.mes}</span>
        <span className={'tl-chip tl-' + (active?.tipo || 'vivo')}>{timelineTypeLabel(active?.tipo)}</span>
        {loading ? <span className="tl-loading">cargando</span> : null}
      </div>
      <span className="tl-tick">{items[0]?.label}</span>
      <div
        className="tl-rail"
        onClick={onRailClick}
        role="slider"
        aria-label="Línea de tiempo de meses"
        aria-valuemin={0}
        aria-valuemax={items.length - 1}
        aria-valuenow={activeIndex}
      >
        <span className="tl-track" />
        <span className="tl-progress" style={{ width: `${pct}%` }} />
        <span className={'tl-handle tl-' + (active?.tipo || 'vivo')} style={{ left: `${pct}%` }} />
      </div>
      <span className="tl-tick">{items[items.length - 1]?.label}</span>
      <div className="tl-controls">
        <button type="button" className="tl-nav" onClick={onPrev} disabled={loading || activeIndex <= 0}><i className="pi pi-chevron-left" /></button>
        <button type="button" className="tl-nav" onClick={onNext} disabled={loading || activeIndex >= items.length - 1}><i className="pi pi-chevron-right" /></button>
        <button type="button" className="tl-today" onClick={onToday} disabled={loading || active?.tipo === 'vivo'}>Hoy</button>
      </div>
    </motion.div>
  );
}

/* onOpen se dispara con hover Y con clic (respaldo touch); onLeave programa el
 * cierre diferido del popover (el padre lo cancela si el puntero entra al pop). */
function KpiTile({ accent, icon, label, value, unit, delta, deltaDir, onOpen, onLeave }) {
  const clickable = !!onOpen;
  return (
    <motion.div
      className={'kpi accent-' + accent + (clickable ? ' clickable' : '')}
      onClick={onOpen}
      onMouseEnter={onOpen}
      onMouseLeave={onLeave}
      whileHover={clickable ? { y: -3 } : undefined}
      whileTap={clickable ? { scale: 0.985 } : undefined}
    >
      <div className="klabel"><i className={icon} />{label}</div>
      <div className="kval">{value}{unit ? <small> {unit}</small> : null}</div>
      {delta ? (
        <div className={'kdelta ' + (deltaDir || 'up')}>
          {deltaDir === 'down' ? <i className="pi pi-arrow-down" style={{ fontSize: 11 }} />
            : deltaDir === 'muted' ? null
            : <i className="pi pi-arrow-up" style={{ fontSize: 11 }} />}
          {delta}
        </div>
      ) : null}
      {clickable ? <i className="pi pi-chevron-right kpi-go" /> : null}
    </motion.div>
  );
}

export const CarteraDashboard = ({ onGoClientes, onOpenCredito }) => {
  const [kpis, setKpis] = useState(null);
  const [aging, setAging] = useState(null);
  const [porEtapa, setPorEtapa] = useState([]);
  const [cobranza, setCobranza] = useState([]);
  const [resumen, setResumen] = useState([]);
  const [proximos, setProximos] = useState([]);
  const [bandeja, setBandeja] = useState([]);
  const [portafolio, setPortafolio] = useState(null);
  const [morosidadBuckets, setMorosidadBuckets] = useState(null);
  const [evolucion, setEvolucion] = useState([]);
  const [afAmort, setAfAmort] = useState([]);
  const [analitica, setAnalitica] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [activePeriod, setActivePeriod] = useState(null);
  const [snapshotMeta, setSnapshotMeta] = useState({ tipo: 'vivo' });
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [view12m, setView12m] = useState('stacked');
  const [show12mTable, setShow12mTable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  // Popover de drill-down: { open, rect, title, subtitle, accent, rows, emptyText }.
  // Abre por hover; el cierre es diferido (timer) para poder cruzar el hueco
  // ancla → popover sin que se esfume. HOVER-INTENT: la primera apertura es
  // instantánea, pero CAMBIAR de contenido con un popover ya abierto exige
  // ~260ms sostenidos sobre el nuevo ancla — así, ir de un segmento de la dona
  // hacia el popover cruzando otros segmentos NO roba el contenido.
  const [drill, setDrill] = useState({ open: false });
  const drillRef = useRef(drill);
  drillRef.current = drill;
  // Tooltip flotante para gráficas de barras/puntos (estilo mockup).
  const [chartTip, setChartTip] = useState(null);
  const tipEnter = (e, title, lines) => setChartTip({ x: e.clientX, y: e.clientY, title, lines: [].concat(lines).filter(Boolean) });
  const tipMove = (e) => setChartTip((t) => (t ? { ...t, x: e.clientX, y: e.clientY } : t));
  const tipLeave = () => setChartTip(null);
  const hoverTimer = useRef(null);
  const switchTimer = useRef(null);
  const donutRef = useRef(null);
  const compDonutRef = useRef(null);
  const cancelClose = () => clearTimeout(hoverTimer.current);
  const cancelSwitch = () => clearTimeout(switchTimer.current);
  const closeDrill = () => { cancelClose(); cancelSwitch(); setDrill((d) => ({ ...d, open: false })); };
  const scheduleClose = () => {
    cancelClose();
    hoverTimer.current = setTimeout(() => setDrill((d) => ({ ...d, open: false })), 170);
  };
  // El puntero salió de un ancla: cancela cualquier cambio pendiente y programa cierre.
  const leaveTrigger = () => { cancelSwitch(); scheduleClose(); };
  // El puntero está DENTRO del popover: ni cerrar ni cambiar el contenido.
  const stayPopover = () => { cancelClose(); cancelSwitch(); };
  const requestDrill = (rect, cfg) => {
    if (!rect) return;
    cancelClose();
    cancelSwitch();
    const cur = drillRef.current;
    if (cur.open && cur.title !== cfg.title) {
      // Ya hay otro abierto: cambiar solo si el hover se sostiene (intent).
      switchTimer.current = setTimeout(() => setDrill({ open: true, rect, ...cfg }), 260);
      return;
    }
    // Primera apertura (instantánea) o re-hover del mismo: refresca contenido
    // conservando el rect para que el popover no brinque de lugar.
    setDrill({ open: true, rect: cur.open && cur.title === cfg.title ? cur.rect : rect, ...cfg });
  };
  const openDrill = (e, cfg) => requestDrill(e.currentTarget.getBoundingClientRect(), cfg);
  const openDrillRect = (rect, cfg) => requestDrill(rect, cfg);
  useEffect(() => () => { clearTimeout(hoverTimer.current); clearTimeout(switchTimer.current); }, []);

  const applyDashboard = (d, meta = { tipo: 'vivo' }, bandejaRows) => {
    setKpis(d.kpis || null);
    setAging(d.aging ?? null);
    setPorEtapa(d.por_etapa ?? null);
    setCobranza(d.cobranza_proyectada ?? null);
    setResumen(d.creditos_resumen || []);
    setProximos(d.proximos_vencimientos ?? null);
    setBandeja(bandejaRows ?? deriveBandejaFromResumen(d.creditos_resumen || []));
    setPortafolio(d.portafolio || null);
    setMorosidadBuckets(d.morosidad_buckets ?? null);
    setEvolucion(d.evolucion_12m || []);
    setAfAmort(d.af_amortizacion_mensual || []);
    setAnalitica(d.analitica_ap || null);
    setSnapshotMeta(meta || { tipo: 'vivo' });
  };

  useEffect(() => {
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [dash, atrasados, snaps] = await Promise.all([
          carteraApi.getDashboard({}, ctrl.signal),
          // Bandeja: TODOS los vencidos y deteriorados (per_page alto, sin recorte).
          carteraApi.getCartera({ estatus: 'vencido,deteriorado', per_page: 100 }, ctrl.signal),
          carteraApi.getSnapshots(ctrl.signal).catch(() => null),
        ]);
        if (ctrl.signal.aborted) return;
        const d = dash?.data || {};
        const tl = snaps?.data?.data || [];
        const live = tl.find((x) => x.tipo === 'vivo') || tl[tl.length - 1] || null;
        setSnapshots(tl);
        setActivePeriod(live?.mes || new Date().toISOString().slice(0, 7));
        applyDashboard(d, { tipo: 'vivo', periodo: live?.mes, label: live?.label }, atrasados?.data?.data || []);
      } catch (e) {
        if (ctrl.signal.aborted || e?.code === 'ERR_CANCELED') return;
        setError(e?.response?.data?.message || 'No se pudo cargar el tablero.');
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, []);

  const selectPeriod = async (periodo) => {
    if (!periodo || periodo === activePeriod || timelineLoading) return;
    const ctrl = new AbortController();
    abortRef.current?.abort();
    abortRef.current = ctrl;
    setTimelineLoading(true);
    setError(null);
    try {
      const item = snapshots.find((x) => x.mes === periodo);
      if (item?.tipo === 'vivo') {
        const [dash, atrasados] = await Promise.all([
          carteraApi.getDashboard({}, ctrl.signal),
          carteraApi.getCartera({ estatus: 'vencido,deteriorado', per_page: 100 }, ctrl.signal),
        ]);
        if (ctrl.signal.aborted) return;
        applyDashboard(dash?.data || {}, { tipo: 'vivo', periodo, label: item?.label }, atrasados?.data?.data || []);
      } else {
        const snap = await carteraApi.getSnapshot(periodo, ctrl.signal);
        if (ctrl.signal.aborted) return;
        const payload = snap?.data || {};
        applyDashboard(payload.dashboard || {}, payload);
      }
      setActivePeriod(periodo);
      closeDrill();
    } catch (e) {
      if (ctrl.signal.aborted || e?.code === 'ERR_CANCELED') return;
      setError(e?.response?.data?.message || 'No se pudo cargar el periodo seleccionado.');
    } finally {
      if (!ctrl.signal.aborted) setTimelineLoading(false);
    }
  };

  const proximosGroups = useMemo(() => {
    const map = new Map();
    for (const p of (Array.isArray(proximos) ? proximos : [])) {
      if (!map.has(p.fecha_vencimiento)) map.set(p.fecha_vencimiento, []);
      map.get(p.fecha_vencimiento).push(p);
    }
    return Array.from(map.entries())
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .map(([fecha, items]) => ({ fecha, items, total: items.reduce((s, x) => s + (Number(x.monto) || 0), 0) }));
  }, [proximos]);

  if (loading) return <div className="cart-loading">Cargando tablero de cartera…</div>;
  if (error) return <div className="cart-loading">{error}</div>;

  const activeTimeline = snapshots.find((x) => x.mes === activePeriod) || null;
  const activeTipo = snapshotMeta?.tipo || activeTimeline?.tipo || 'vivo';
  const isHistoric = activeTipo !== 'vivo';
  const sinDatoSet = new Set(snapshotMeta?.sin_dato || []);
  const missingBlock = (key, data) => isHistoric && (activeTipo === 'simulado' || sinDatoSet.has(key) || data === null);
  const missingCobranzaKpis = missingBlock('kpis', kpis)
    || !hasNumber(kpis?.expuesto_mora)
    || !hasNumber(kpis?.saldo_riesgo)
    || !hasNumber(kpis?.monto_vencido);
  const missingAging = missingBlock('aging', aging);
  const missingBuckets = missingBlock('morosidad_buckets', morosidadBuckets);
  const missingEtapas = missingBlock('por_etapa', porEtapa);
  const missingCobranza = missingBlock('cobranza_proyectada', cobranza);
  const missingProximos = missingBlock('proximos_vencimientos', proximos);
  const missingBandeja = isHistoric && (missingCobranzaKpis || missingBlock('creditos_resumen', resumen));
  const periodoLabel = monthLabelLong(activePeriod || snapshotMeta?.periodo);
  const timelineIndex = snapshots.findIndex((x) => x.mes === activePeriod);
  const livePeriod = snapshots.find((x) => x.tipo === 'vivo')?.mes || snapshots[snapshots.length - 1]?.mes;
  const selectPrev = () => timelineIndex > 0 && selectPeriod(snapshots[timelineIndex - 1].mes);
  const selectNext = () => timelineIndex >= 0 && timelineIndex < snapshots.length - 1 && selectPeriod(snapshots[timelineIndex + 1].mes);
  const selectToday = () => livePeriod && selectPeriod(livePeriod);

  const porEstatus = kpis?.por_estatus || {};
  const totalContratos = Object.values(porEstatus).reduce((s, x) => s + (x?.count || 0), 0) || resumen.length;
  const vigente = porEstatus.vigente || { count: 0, monto: 0 };
  const moraTotal = hasNumber(kpis?.mora_total) ? Number(kpis.mora_total) : null;
  const expuestoMora = hasNumber(kpis?.expuesto_mora) ? Number(kpis.expuesto_mora) : null;
  const saldoRiesgo = hasNumber(kpis?.saldo_riesgo) ? Number(kpis.saldo_riesgo) : null;
  const contratosMora = kpis?.contratos_mora || { count: 0, total: 0 };
  const morosidad = Number(kpis?.indice_morosidad || 0);
  const compo = (kpis?.por_tipo || []).map((t) => `${t.count} ${t.label}`).join(' · ');

  // ── Listas para drill-down (a partir del resumen compacto) ──
  const byDias = (a, b) => (Number(b.dias_vencido) || 0) - (Number(a.dias_vencido) || 0);
  const bySaldo = (a, b) => (Number(b.saldo_insoluto) || 0) - (Number(a.saldo_insoluto) || 0);
  const enMoraList = resumen.filter((c) => c.estatus === 'vencido' || c.estatus === 'deteriorado').slice().sort(byDias);
  const vigentesList = resumen.filter((c) => c.estatus === 'vigente').slice().sort(bySaldo);
  const allList = resumen.slice().sort(bySaldo);
  const byBucket = (key) => resumen.filter((c) => c.bucket === key).slice().sort(byDias);
  const byEtapa = (key) => resumen.filter((c) => c.etapa_cobranza === key).slice().sort(byDias);

  const plural = (n, s, p) => `${n} ${n === 1 ? s : p}`;
  const mkRow = (c, variant) => {
    const base = { id: c.id, cliente: c.cliente, sub: `${c.clave_contrato} · ${c.tipo_label}`, dotColor: ESTATUS_DOT[c.estatus] };
    if (variant === 'mora') {
      return { ...base, right: money(c.mora_acumulada), rightNeg: true, rightSub: `${plural(c.meses_vencidos, 'mes', 'meses')} · ${money0(c.monto_vencido)} venc.` };
    }
    if (variant === 'saldo') return { ...base, right: money0(c.saldo_insoluto), rightSub: `${c.dias_vencido} días atraso` };
    if (variant === 'contratos') return { ...base, right: `${c.dias_vencido} días`, rightSub: ETAPA_LABELS[c.etapa_cobranza] || ETAPA_LABELS[c.estatus] };
    if (variant === 'bucket') return { ...base, right: money0(c.monto_vencido ?? c.saldo_insoluto), rightSub: c.dias_vencido > 0 ? `${c.dias_vencido} días` : 'al corriente' };
    return { ...base, right: money0(c.saldo_insoluto), rightSub: ETAPA_LABELS[c.estatus] || c.estatus };
  };

  const bucketCfg = (key) => {
    const def = AGING_DEFS.find((d) => d.key === key) || {};
    const list = byBucket(key);
    return {
      title: def.label || 'Contratos', subtitle: `${def.rango || ''} · ${plural(list.length, 'contrato', 'contratos')}`,
      accent: def.color, rows: list.map((c) => mkRow(c, 'bucket')), emptyText: 'Ningún contrato en este rango.',
    };
  };
  const etapaCfg = (key) => {
    const list = byEtapa(key);
    return {
      title: ETAPA_LABELS[key] || key, subtitle: plural(list.length, 'contrato', 'contratos'),
      accent: ETAPA_COLORS[key], rows: list.map((c) => mkRow(c, 'contratos')), emptyText: '—',
    };
  };

  // ── Portafolio (strip + fila por producto + gráficas nuevas) ──
  const byTipoLabel = (lbl) => resumen
    .filter((c) => c.tipo === lbl || c.tipo_label === lbl || c.tipo_label === PROD_NAMES[lbl])
    .slice()
    .sort(bySaldo);
  const prodCfg = (lbl, nombre, color) => {
    const list = byTipoLabel(lbl);
    return {
      title: nombre, subtitle: plural(list.length, 'contrato', 'contratos'), accent: color,
      rows: list.map((c) => mkRow(c, 'all')), emptyText: 'Sin contratos de este producto.',
    };
  };
  const vencSub = (p) => {
    if (!hasNumber(p?.vencida)) return { txt: HISTORIC_EMPTY, neg: false, muted: true };
    return Number(p.vencida) > 0
      ? { txt: `Vencida: ${money0(p.vencida)} (${p.pct_vencida}%)`, neg: true }
      : { txt: 'Todo vigente', neg: false };
  };
  const hasSim = evolucion.some((m) => m.simulado);
  const avg12 = (key) => (evolucion.length
    ? evolucion.reduce((s, m) => s + (Number(m[key]) || 0), 0) / evolucion.length
    : 0);
  // AF amortización: desde el mes actual, 12 meses a la vista.
  const ymCorte = activePeriod || new Date().toISOString().slice(0, 7);
  const afFuturo = afAmort.filter((m) => m.mes >= ymCorte);
  const afVista = afFuturo.slice(0, 12);
  const composicion = (kpis?.por_tipo || []).map((t) => ({
    key: t.tipo || t.label, label: t.label, color: PROD_COLORS[t.tipo] || PROD_COLORS[t.label] || '#64748B',
    count: Number(t.saldo) || 0, contratos: t.count,
  }));
  const compSaldoTotal = composicion.reduce((s, t) => s + t.count, 0);
  const resumenById = new Map(resumen.map((c) => [String(c.id), c]));
  const bucketRows = Array.isArray(morosidadBuckets) ? morosidadBuckets : [];
  const bucketTotalMonto = bucketRows.reduce((s, b) => s + (Number(b.monto) || 0), 0);
  const antiguedadBuckets = bucketRows.map((b, i) => {
    const color = BUCKET_COLORS[i] || '#64748B';
    const monto = Number(b.monto) || 0;
    const ids = Array.isArray(b.creditos) ? b.creditos : [];
    const rows = ids.map((id) => resumenById.get(String(id))).filter(Boolean);
    return {
      ...b,
      color,
      monto,
      rows,
      pct: bucketTotalMonto > 0 ? (monto / bucketTotalMonto) * 100 : 0,
      empty: !hasNumber(b.monto) || monto <= 0,
    };
  });
  const antiguedadCfg = (b) => ({
    title: `Vencida · ${b.label}`,
    subtitle: `${plural(Number(b.count) || 0, 'contrato', 'contratos')} · ${money0(b.monto)}`,
    accent: b.color,
    rows: b.rows.map((c) => mkRow(c, 'bucket')),
    emptyText: 'Sin contratos identificados para este rango.',
  });

  // ── Analítica AP (calidad, scatter, rendimiento, extinción, concentración) ──
  const MARCA_COLORS = ['#34C6F1', '#00BA6E', '#21588C', '#F0961E', '#E5631B', '#0C2036', '#94A3B8'];
  const aKpis = analitica?.kpis;
  const marcasDonut = (analitica?.marcas || []).map((m, i) => ({
    key: m.label, label: m.label, color: MARCA_COLORS[i % MARCA_COLORS.length], count: m.count,
  }));
  // Extinción: 40+ puntos mensuales — etiquetas solo cada 6 meses (y la última).
  const extPts = (analitica?.extincion || []).map((p, i, arr) => ({
    ...p, label: i % 6 === 0 || i === arr.length - 1 ? p.label : '',
  }));
  const extStats = analitica?.extincion_stats;
  const scatterPts = (analitica?.scatter || []).map((p) => ({
    id: p.id, clave: p.clave, cliente: p.cliente, x: p.meses_rem, y: p.vf,
  }));
  const rentaVfProm = Number(aKpis?.renta_vf_pct || 0);
  const pctFmt = (v) => (Number(v) || 0).toFixed(2) + '%';
  const rentaVfBars = (analitica?.renta_vf || []).map((r) => ({
    key: r.clave, label: r.clave, count: '', pct: r.pct,
    color: r.pct >= rentaVfProm ? '#00BA6E' : '#F0961E',
  }));

  // Concentración top 5 clientes vs resto (agregado en el front desde el resumen).
  const shortName = (s = '') => (s.length > 24 ? s.slice(0, 22) + '…' : s);
  const concMap = new Map();
  resumen.forEach((c) => {
    const k = c.cliente || '—';
    const cur = concMap.get(k) || { cliente: k, saldo: 0, items: [] };
    cur.saldo += Number(c.saldo_insoluto) || 0;
    cur.items.push(c);
    concMap.set(k, cur);
  });
  const concOrden = [...concMap.values()].sort((a, b) => b.saldo - a.saldo);
  const CONC_COLORS = ['#0C2036', '#21588C', '#2BA7CE', '#34C6F1', '#7DD3E8'];
  const concResto = concOrden.slice(5);
  const concBars = [
    ...concOrden.slice(0, 5).map((c, i) => ({
      key: c.cliente, label: shortName(c.cliente), color: CONC_COLORS[i],
      count: c.items.length, saldo: c.saldo, items: c.items,
    })),
    ...(concResto.length ? [{
      key: 'resto', label: `Resto (${concResto.length} clientes)`, color: '#CBD5E1',
      count: concResto.reduce((s, x) => s + x.items.length, 0),
      saldo: concResto.reduce((s, x) => s + x.saldo, 0),
      items: concResto.flatMap((x) => x.items),
    }] : []),
  ];
  const concTop5Pct = compSaldoTotal > 0
    ? Math.round((concOrden.slice(0, 5).reduce((s, x) => s + x.saldo, 0) / compSaldoTotal) * 100)
    : 0;
  const concCfg = (d) => ({
    title: d.label, subtitle: `${plural(d.count, 'contrato', 'contratos')} · ${money0(d.saldo)}`,
    accent: d.color, rows: d.items.map((c) => mkRow(c, 'all')), emptyText: '—',
  });

  const agingRows = AGING_DEFS.map((d) => ({ ...d, count: aging?.[d.key]?.count || 0, monto: aging?.[d.key]?.monto || 0 }));
  const agingTotal = agingRows.reduce((s, r) => s + r.count, 0);
  const etapaData = (Array.isArray(porEtapa) ? porEtapa : []).map((e) => ({ key: e.etapa, label: ETAPA_LABELS[e.etapa] || e.etapa, color: ETAPA_COLORS[e.etapa], count: e.count, saldo: e.saldo, mora: e.mora }));
  const cobranzaRows = Array.isArray(cobranza) ? cobranza : [];
  const proximosRows = Array.isArray(proximos) ? proximos : [];
  const fechaTxt = periodoLabel;
  const reveal = (n) => ({ initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5, delay: n * 0.09, ease: [0.16, 1, 0.3, 1] } });

  return (
    <>
      <TimelineBar
        items={snapshots}
        activeMes={activePeriod}
        loading={timelineLoading}
        onSelect={selectPeriod}
        onPrev={selectPrev}
        onNext={selectNext}
        onToday={selectToday}
      />
      {isHistoric ? (
        <motion.div className={'history-banner ' + activeTipo} initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
          <i className="pi pi-calendar" />
          <span>
            Viendo el cierre de {periodoLabel} · {activeTipo === 'real'
              ? `foto congelada${captureLabel(snapshotMeta?.capturado_en) ? ` · capturada ${captureLabel(snapshotMeta.capturado_en)}` : ''}`
              : 'vista reconstruida del calendario · solo lectura'}
          </span>
          <button type="button" onClick={selectToday}>Volver a hoy</button>
        </motion.div>
      ) : null}

      {/* ══ Page head ══ */}
      <div className="dc-head" {...reveal(0)}>
        <div>
          <div className="dc-eyebrow">Cobranza · {fechaTxt}</div>
          <h1 className="dc-title">Tablero de cartera</h1>
          <p className="dc-sub">Estado de los {totalContratos} contratos · arrendamiento puro, financiero y crédito simple</p>
        </div>
        <div className="spacer" />
        {isHistoric ? (
          <button className="dc-btn dc-btn-cyan" onClick={selectToday}><i className="pi pi-arrow-left" />Volver a hoy</button>
        ) : (
          <>
            <button className="dc-btn dc-btn-ghost"><i className="pi pi-download" />Exportar</button>
            <button className="dc-btn dc-btn-green" onClick={onGoClientes}><i className="pi pi-plus" />Aplicar pago</button>
          </>
        )}
      </div>

      {/* ══ Strip de portafolio ══ */}
      {portafolio ? (
        <motion.div className="dc-strip" {...reveal(0.5)}>
          <button type="button" className="dc-total"
            onMouseLeave={leaveTrigger}
            onClick={(e) => openDrill(e, { title: 'Portafolio total', subtitle: `${totalContratos} contratos`, accent: 'var(--pei-cyan)', rows: allList.map((c) => mkRow(c, 'all')) })}
            onMouseEnter={(e) => openDrill(e, { title: 'Portafolio total', subtitle: `${totalContratos} contratos`, accent: 'var(--pei-cyan)', rows: allList.map((c) => mkRow(c, 'all')) })}>
            <span className="dc-total-bar" />
            <img className="dc-total-mark" src={peicarMark} alt="" aria-hidden="true" />
            <div className="dc-total-label">Portafolio total Peicar</div>
            <div className="dc-total-val">{moneyM(portafolio.total)}</div>
            <div className="dc-total-foot">
              {hasNumber(portafolio.vigente) ? <>Vigente <b>{moneyM(portafolio.vigente)}</b></> : null}
              {hasNumber(portafolio.vencida) && Number(portafolio.vencida) > 0
                ? <span className="dc-pill-venc">Vencida {money0(portafolio.vencida)}</span>
                : hasNumber(portafolio.vencida)
                  ? <span className="dc-pill-ok">Todo vigente</span>
                  : <span className="dc-pill-mut">Sin dato histórico</span>}
            </div>
          </button>
          {[
            { key: 'AP', label: 'AP — Valor factura', sub: `${portafolio.ap.contratos} contratos · VF s/IVA`, data: portafolio.ap },
            { key: 'AF', label: 'AF — Capital', sub: `${portafolio.af.contratos} contratos activos`, data: portafolio.af },
            { key: 'CS', label: 'CS — Saldo', sub: `${portafolio.cs.contratos} contratos`, data: portafolio.cs },
          ].map((p) => {
            const sub = vencSub(p.data);
            return (
              <button type="button" className="dc-prod" key={p.key} style={{ borderTopColor: PROD_COLORS[p.key] }}
                onMouseLeave={leaveTrigger}
                onClick={(e) => openDrill(e, prodCfg(p.key, PROD_NAMES[p.key] || p.key, PROD_COLORS[p.key]))}
                onMouseEnter={(e) => openDrill(e, prodCfg(p.key, PROD_NAMES[p.key] || p.key, PROD_COLORS[p.key]))}>
                <div className="dc-prod-label" style={{ color: PROD_COLORS[p.key] }}>{p.label}</div>
                <div className="dc-prod-val">{moneyM(p.data.total)}</div>
                <div className="dc-prod-sub">{p.sub}</div>
                <div className={'dc-prod-venc' + (sub.neg ? '' : ' ok')}>
                  <i className={sub.neg ? 'pi pi-exclamation-triangle' : 'pi pi-check-circle'} />
                  {sub.muted ? HISTORIC_EMPTY : sub.txt}
                </div>
              </button>
            );
          })}
          <div className="dc-libros">
            <div className="dc-prod-label" style={{ color: 'var(--ink-4)' }}>AP — Valor libros</div>
            <div className="dc-libros-val">Próximamente</div>
            <div className="dc-prod-sub">depreciación · en integración</div>
          </div>
          <button type="button" className="dc-prod" style={{ borderTopColor: 'var(--pei-navy)' }}
            onMouseLeave={leaveTrigger}
            onClick={(e) => openDrill(e, prodCfg('AP', 'AP — Rentas futuras', 'var(--pei-navy)'))}
            onMouseEnter={(e) => openDrill(e, prodCfg('AP', 'AP — Rentas futuras', 'var(--pei-navy)'))}>
            <div className="dc-prod-label" style={{ color: 'var(--pei-navy)' }}>AP — Rentas futuras</div>
            <div className="dc-prod-val">{moneyM(portafolio.ap.rentas_futuras)}</div>
            <div className="dc-prod-sub">c/IVA incl. residuales</div>
            <div className="dc-prod-venc ok" style={{ color: 'var(--pei-steel)' }}>Flujo contratado por entrar</div>
          </button>
        </motion.div>
      ) : null}

      {/* ══ KPIs de cobranza (debajo del strip, como el mockup) ══ */}
      <motion.div className="kpi-grid kpi-grid-5" {...reveal(0.6)}>
        <KpiTile accent="cyan" icon="pi pi-wallet" label="Cartera administrada"
          value={<CountUp value={kpis?.cartera_total} format={money0} />} delta={compo || `${totalContratos} contratos`} deltaDir="muted" onLeave={leaveTrigger}
          onOpen={(e) => openDrill(e, { title: 'Cartera administrada', subtitle: `${totalContratos} contratos · ${money0(kpis?.cartera_total)}`, accent: 'var(--pei-cyan)', rows: allList.map((c) => mkRow(c, 'all')) })} />
        <KpiTile accent="green" icon="pi pi-check-circle" label="Al corriente"
          value={missingCobranzaKpis || !hasNumber(vigente.monto) ? <span className="k-missing">Sin dato</span> : <CountUp value={vigente.monto} format={money0} />}
          delta={missingCobranzaKpis || !hasNumber(vigente.monto) ? HISTORIC_EMPTY : `${vigente.count} contratos`} deltaDir={missingCobranzaKpis ? 'muted' : 'up'} onLeave={leaveTrigger}
          onOpen={missingCobranzaKpis ? undefined : (e) => openDrill(e, { title: 'Al corriente', subtitle: plural(vigentesList.length, 'contrato', 'contratos'), accent: 'var(--et-corriente)', rows: vigentesList.map((c) => mkRow(c, 'all')), emptyText: 'Ningún contrato al corriente.' })} />
        <KpiTile accent="red" icon="pi pi-exclamation-triangle" label="Vencido + moratorios"
          value={missingCobranzaKpis ? <span className="k-missing">Sin dato</span> : <CountUp value={expuestoMora} format={money0} />}
          delta={missingCobranzaKpis ? HISTORIC_EMPTY : `Moratorios: ${money0(moraTotal)}`} deltaDir="down" onLeave={leaveTrigger}
          onOpen={missingCobranzaKpis ? undefined : (e) => openDrill(e, { title: 'Vencido + moratorios', subtitle: `${plural(enMoraList.length, 'contrato', 'contratos')} · ${money0(expuestoMora)} exigible`, accent: 'var(--et-judicial)', rows: enMoraList.map((c) => mkRow(c, 'mora')), emptyText: 'Nada vencido. Todo al corriente.' })} />
        <KpiTile accent="orange" icon="pi pi-flag" label="Saldo en riesgo"
          value={missingCobranzaKpis ? <span className="k-missing">Sin dato</span> : <CountUp value={saldoRiesgo} format={money0} />}
          delta={missingCobranzaKpis ? HISTORIC_EMPTY : 'Insoluto, incluye deuda no vencida'} deltaDir="muted" onLeave={leaveTrigger}
          onOpen={missingCobranzaKpis ? undefined : (e) => openDrill(e, { title: 'Saldo en riesgo', subtitle: `${plural(enMoraList.length, 'contrato', 'contratos')} con atraso`, accent: 'var(--et-extra)', rows: enMoraList.map((c) => mkRow(c, 'saldo')), emptyText: 'Sin contratos en riesgo.' })} />
        <KpiTile accent="amber" icon="pi pi-users" label="Contratos en mora"
          value={missingCobranzaKpis ? <span className="k-missing">Sin dato</span> : <CountUp value={contratosMora.count} format={(v) => Math.round(v)} />}
          unit={missingCobranzaKpis ? null : `de ${contratosMora.total || totalContratos}`}
          delta={missingCobranzaKpis ? HISTORIC_EMPTY : `${morosidad}% de la cartera`} deltaDir="down" onLeave={leaveTrigger}
          onOpen={missingCobranzaKpis ? undefined : (e) => openDrill(e, { title: 'Contratos en mora', subtitle: `${morosidad}% de la cartera`, accent: 'var(--et-vencida)', rows: enMoraList.map((c) => mkRow(c, 'contratos')), emptyText: 'Ningún contrato en mora.' })} />
      </motion.div>

      {/* ══ Antigüedad de la vencida ══ */}
      <motion.div className="dc-card" {...reveal(0.7)}>
        <div className="dc-card-h">
          <div className="dc-card-title">Antigüedad de la vencida</div>
          <div className="dc-card-note">días desde el primer recibo vencido · pasa el cursor para ver contratos</div>
          <div className="spacer" />
          <span className="dc-chip-red">{hasNumber(kpis?.monto_vencido) ? `${money0(kpis.monto_vencido)} exigible vencido` : 'sin dato histórico'}</span>
        </div>
        {missingBuckets ? (
          <HistoricalEmpty icon="pi pi-clock" />
        ) : antiguedadBuckets.length === 0 || bucketTotalMonto <= 0 ? (
          <div className="empty-state"><i className="pi pi-check-circle" /><div className="es-title">Sin cartera vencida</div><div className="es-sub">No hay buckets de antigüedad activos.</div></div>
        ) : (
          <>
            <div className="dc-seg-bar">
              {antiguedadBuckets.filter((b) => b.monto > 0).map((b) => (
                <span key={b.label} style={{ width: `${b.pct}%`, background: b.color }} title={`${b.label} · ${money0(b.monto)}`} />
              ))}
            </div>
            <div className="dc-bucket-grid">
              {antiguedadBuckets.map((b) => (
                <button type="button" className="dc-bucket" key={b.label}
                  style={{ borderColor: `${b.color}55`, background: `${b.color}14`, opacity: b.empty ? 0.5 : 1 }}
                  onClick={(e) => openDrill(e, antiguedadCfg(b))}
                  onMouseEnter={(e) => openDrill(e, antiguedadCfg(b))}
                  onMouseLeave={leaveTrigger}>
                  <div className="dc-bucket-rango" style={{ color: b.color }}>{b.label}</div>
                  <div className="dc-bucket-count">{Number(b.count) || 0}</div>
                  <div className="dc-bucket-monto">{b.monto > 0 ? money0(b.monto) : '—'}</div>
                </button>
              ))}
            </div>
          </>
        )}
      </motion.div>

      {/* ══ Salud y composición ══ */}
      <motion.div className="dc-card" {...reveal(1)}>
        <div className="dc-card-h">
          <div className="dc-card-title">Salud y composición del portafolio</div>
          <div className="dc-card-note">pasa el cursor sobre cada renglón para ver contratos</div>
        </div>
        <div className="dc-health">
          <div className="dc-health-col dc-health-l">
            <div className="dc-panel-title">Semáforo · días de atraso</div>
            {missingAging ? <HistoricalEmpty icon="pi pi-history" /> : (
              <div className="dc-donut-block">
                <div ref={donutRef} onMouseLeave={leaveTrigger}>
                  <AnimatedDonut rows={agingRows} total={agingTotal}
                    onSegment={(key) => openDrillRect(donutRef.current?.getBoundingClientRect(), bucketCfg(key))}
                    onSegmentHover={(key) => openDrillRect(donutRef.current?.getBoundingClientRect(), bucketCfg(key))} />
                </div>
                <div className="dc-rows">
                  {agingRows.map((r) => (
                    <button type="button" className="dc-row" key={r.key}
                      onClick={(e) => openDrill(e, bucketCfg(r.key))}
                      onMouseEnter={(e) => openDrill(e, bucketCfg(r.key))}
                      onMouseLeave={leaveTrigger}>
                      <span className="dc-dot" style={{ background: r.color }} />
                      <span className="dc-row-lbl">{r.label}</span>
                      <span className="dc-row-rango">{r.rango}</span>
                      <span className="spacer" />
                      <span className="dc-row-ct">{r.count}</span>
                      <span className="dc-row-amt">{money0(r.monto)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="dc-health-col dc-health-r">
            <div className="dc-panel-title">Composición · saldo administrado</div>
            <div className="dc-donut-block">
              <div ref={compDonutRef} onMouseLeave={leaveTrigger}>
                <AnimatedDonut rows={composicion} total={compSaldoTotal}
                  centerValue={totalContratos} centerLabel="contratos"
                  onSegment={(key) => openDrillRect(compDonutRef.current?.getBoundingClientRect(), prodCfg(key, PROD_NAMES[key] || key, PROD_COLORS[key]))}
                  onSegmentHover={(key) => openDrillRect(compDonutRef.current?.getBoundingClientRect(), prodCfg(key, PROD_NAMES[key] || key, PROD_COLORS[key]))} />
              </div>
              <div className="dc-rows">
                {composicion.map((t) => (
                  <button type="button" className="dc-row" key={t.key}
                    onClick={(e) => openDrill(e, prodCfg(t.key, PROD_NAMES[t.key] || t.label, t.color))}
                    onMouseEnter={(e) => openDrill(e, prodCfg(t.key, PROD_NAMES[t.key] || t.label, t.color))}
                    onMouseLeave={leaveTrigger}>
                    <span className="dc-dot" style={{ background: t.color }} />
                    <span className="dc-row-lbl">{t.label}</span>
                    <span className="dc-row-rango">{plural(t.contratos, 'contrato', 'contratos')}</span>
                    <span className="spacer" />
                    <span className="dc-row-ct">{compSaldoTotal > 0 ? Math.round((t.count / compSaldoTotal) * 100) + '%' : '—'}</span>
                    <span className="dc-row-amt">{money0(t.count)}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="dc-health-foot">
          <div>
            <div className="dc-foot-lbl" style={{ color: '#DC2626' }}>Vencido + moratorios</div>
            <div className="dc-foot-val" style={{ color: '#DC2626' }}>{missingCobranzaKpis ? 'Sin dato' : money0(expuestoMora)}</div>
            <div className="dc-foot-sub">{missingCobranzaKpis ? HISTORIC_EMPTY : 'exigible hoy'}</div>
          </div>
          <div>
            <div className="dc-foot-lbl" style={{ color: '#EA7A1F' }}>Saldo en riesgo</div>
            <div className="dc-foot-val" style={{ color: '#EA7A1F' }}>{missingCobranzaKpis ? 'Sin dato' : money0(saldoRiesgo)}</div>
            <div className="dc-foot-sub">{missingCobranzaKpis ? HISTORIC_EMPTY : 'insoluto con atraso'}</div>
          </div>
          <div className="spacer" />
          {!isHistoric ? <button className="dc-btn dc-btn-ghost" onClick={onGoClientes}>Ir a gestión<i className="pi pi-arrow-right" /></button> : null}
        </div>
      </motion.div>

      {/* ══ Evolución de saldos — últimos 12 meses (snapshots reales + meses
          simulados reconstruidos del calendario; sin depreciación por ahora) ══ */}
      {evolucion.length > 0 ? (
        <motion.div className="cart-row cart-row-full" {...reveal(1.5)}>
          <div className="card card-pad">
            <div className="card-h">
              <div>
                <h3>Evolución de saldos — últimos 12 meses</h3>
                <span className="sub">AP rentas por cobrar · AF capital · CS saldo</span>
              </div>
              <div className="spacer" />
              <div className="seg-toggle">
                {[['stacked', 'Apilada'], ['line', 'Líneas'], ['grouped', 'Barras']].map(([k, lbl]) => (
                  <button key={k} type="button" className={view12m === k ? 'on' : ''}
                    onClick={() => setView12m(k)}>{lbl}</button>
                ))}
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShow12mTable((v) => !v)}>
                <i className="pi pi-table" />{show12mTable ? 'Ocultar tabla' : 'Ver tabla'}
              </button>
            </div>
            {view12m === 'line' ? (
              <div style={{ marginTop: 18 }}><MultiSeriesChart data={evolucion} series={S12M_SERIES} mode="line" /></div>
            ) : (() => {
              const max = Math.max(1, ...evolucion.map((m) => Number(m.total) || 0));
              const sc = 196 / max;
              const stacked = view12m === 'stacked';
              return (
                <div className="dc-evo">
                  {evolucion.map((m, i) => {
                    const cur = i === evolucion.length - 1;
                    return (
                      <div className="dc-evo-col" key={m.mes}
                        onMouseEnter={(e) => tipEnter(e, `${m.label}${m.simulado ? ' · simulado' : ''}`, [`Total: ${money0(m.total)}`, `AP: ${money0(m.ap)}`, `AF: ${money0(m.af)}`, `CS: ${money0(m.cs)}`])}
                        onMouseMove={tipMove} onMouseLeave={tipLeave}>
                        <div className="dc-evo-top" style={{ fontWeight: cur ? 700 : 500, color: cur ? '#0C2036' : '#94A3B8' }}>{moneyM(m.total)}</div>
                        <div className="dc-evo-track">
                          <div className="dc-evo-fill">
                            {stacked ? (
                              <>
                                <div style={{ height: (Number(m.cs) || 0) * sc, background: '#21588C' }} />
                                <div style={{ height: (Number(m.af) || 0) * sc, background: '#00BA6E' }} />
                                <div style={{ height: (Number(m.ap) || 0) * sc, background: '#34C6F1' }} />
                              </>
                            ) : (
                              <div style={{ height: (Number(m.total) || 0) * sc, background: '#34C6F1' }} />
                            )}
                          </div>
                        </div>
                        <div className="dc-evo-lbl" style={{ fontWeight: cur ? 700 : 500, color: cur ? '#0C2036' : '#94A3B8', borderBottom: m.simulado ? '1px dashed #94A3B8' : 'none' }}>{m.label}</div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            <div className="dc-evo-legend">
              <span><span className="sw" style={{ background: '#34C6F1' }} />AP rentas por cobrar</span>
              <span><span className="sw" style={{ background: '#00BA6E' }} />AF capital</span>
              <span><span className="sw" style={{ background: '#21588C' }} />CS saldo</span>
              <span className="spacer" />
              {hasSim ? (
                <>
                  <span className="dc-chip-sim">simulado</span>
                  <span className="dc-evo-note">mes reconstruido del calendario — aún sin snapshot real</span>
                </>
              ) : null}
            </div>
            {show12mTable ? (
              <>
                <div className="divider" />
                <table className="tbl s12m-table">
                  <thead>
                    <tr>
                      <th>Mes</th>
                      <th className="r">AP rentas por cobrar</th>
                      <th className="r">AF capital</th>
                      <th className="r">CS saldo</th>
                      <th className="r">Total</th>
                      <th className="r">Compras AP</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {evolucion.map((m, i) => (
                      <tr key={m.mes} style={i === evolucion.length - 1 ? { background: 'var(--et-preventiva-bg)' } : undefined}>
                        <td style={{ fontWeight: 700 }}>{m.label}{i === evolucion.length - 1 ? ' ◀ actual' : ''}</td>
                        <td className="r">{money0(m.ap)}</td>
                        <td className="r">{money0(m.af)}</td>
                        <td className="r">{money0(m.cs)}</td>
                        <td className="r" style={{ fontWeight: 700 }}>{money0(m.total)}</td>
                        <td className="r">{m.compras > 0 ? money0(m.compras) : '—'}</td>
                        <td>{m.simulado ? <span className="chip-sim">simulado</span> : null}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>PROMEDIO</td>
                      <td className="r">{money0(avg12('ap'))}</td>
                      <td className="r">{money0(avg12('af'))}</td>
                      <td className="r">{money0(avg12('cs'))}</td>
                      <td className="r">{money0(avg12('total'))}</td>
                      <td className="r">{money0(avg12('compras'))}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </>
            ) : null}
          </div>
        </motion.div>
      ) : null}

      {/* ══ AF amortización mensual ══ */}
      <motion.div className="cart-row cart-row-full" {...reveal(1.8)}>
        <div className="card card-pad">
          <div className="card-h">
            <h3>AF — amortización mensual</h3>
            <span className="sub">capital e interés programados · próximos 12 meses{afFuturo.length > 12 ? ` de ${afFuturo.length}` : ''}</span>
          </div>
          {afVista.length === 0 ? (
            <div className="empty-state"><i className="pi pi-chart-bar" /><div className="es-title">Sin parcialidades programadas</div></div>
          ) : (
            <>
              <div className="dc-vbars" style={{ height: 200 }}>
                {(() => {
                  const maxAf = Math.max(1, ...afVista.map((m) => (Number(m.capital) || 0) + (Number(m.interes) || 0)));
                  return afVista.map((m, i) => {
                    const cap = Number(m.capital) || 0;
                    const int = Number(m.interes) || 0;
                    return (
                      <div className="dc-vcol" key={m.mes || i}
                        onMouseEnter={(e) => tipEnter(e, m.label, [`Capital: ${money0(cap)}`, `Interés + IVA: ${money0(int)}`, `Total: ${money0(cap + int)}`])}
                        onMouseMove={tipMove} onMouseLeave={tipLeave}>
                        <div className="dc-vtop">{moneyM(cap + int)}</div>
                        <div className="dc-vgrow">
                          <div className="dc-vstack">
                            <div style={{ height: `${int / maxAf * 150}px`, background: '#F0961E' }} />
                            <div style={{ height: `${cap / maxAf * 150}px`, background: PROD_COLORS.AF, borderRadius: '0 0 4px 4px' }} />
                          </div>
                        </div>
                        <div className="dc-vlbl">{m.label}</div>
                      </div>
                    );
                  });
                })()}
              </div>
              <div className="dc-evo-legend">
                <span><span className="sw" style={{ background: PROD_COLORS.AF }} />Capital</span>
                <span><span className="sw" style={{ background: '#F0961E' }} />Interés + IVA</span>
              </div>
            </>
          )}
        </div>
      </motion.div>

      {/* ══ Calidad de cartera AP: KPIs de deterioro + 3 minigráficas ══ */}
      {aKpis ? (
        <motion.div className="cart-row cart-row-full" {...reveal(1.9)}>
          <div className="card card-pad">
            <div className="card-h">
              <div>
                <h3>Calidad de cartera AP — indicadores</h3>
                <span className="sub">WAL · vida consumida · rendimiento · residuales · plazos</span>
              </div>
              <div className="spacer" />
              <span className="tag"><i className="pi pi-car" style={{ fontSize: 11 }} />Arrendamiento puro</span>
            </div>
            <div className="stat-tiles">
              <div className="stat-tile">
                <div className="slabel">WAL ponderada</div>
                <div className="sval">{aKpis.wal_meses}m</div>
                <div className="ssub">vida remanente</div>
              </div>
              <div className="stat-tile">
                <div className="slabel">% vida consumida</div>
                <div className="sval">{aKpis.vida_consumida_pct}%</div>
                <div className="ssub">promedio portafolio</div>
              </div>
              <div className="stat-tile">
                <div className="slabel">Renta/VF prom.</div>
                <div className="sval">{aKpis.renta_vf_pct}%</div>
                <div className="ssub">mensual sobre VF c/IVA</div>
              </div>
              <div className="stat-tile">
                <div className="slabel">VR/VF prom.</div>
                <div className="sval">{aKpis.vr_vf_pct}%</div>
                <div className="ssub">valor residual</div>
              </div>
              <div className={'stat-tile ' + (aKpis.tickets_6m?.count > 0 ? 'alert' : 'ok')}>
                <div className="slabel">Vencen ≤6m</div>
                <div className="sval">{aKpis.tickets_6m?.count || 0}</div>
                <div className="ssub">{aKpis.tickets_6m?.count > 0 ? aKpis.tickets_6m.claves.join(', ') : 'ninguno próximo'}</div>
              </div>
              <div className="stat-tile">
                <div className="slabel">Tasas</div>
                <div className="sval">{aKpis.tasa_min != null ? `${Math.round(aKpis.tasa_min)}–${Math.round(aKpis.tasa_max)}%` : '—'}</div>
                <div className="ssub">rango del portafolio</div>
              </div>
            </div>
            <div className="mini-charts">
              <div className="mini-chart">
                <h4>Vida remanente (contratos)</h4>
                <div className="dc-vbars" style={{ height: 118 }}>
                  {(() => {
                    const rows = analitica.remanente_buckets || [];
                    const maxV = Math.max(1, ...rows.map((b) => Number(b.count) || 0));
                    return rows.map((b) => (
                      <div className="dc-vcol" key={b.label}
                        onMouseEnter={(e) => tipEnter(e, `Vida remanente · ${b.label}`, `${b.count} contratos`)}
                        onMouseMove={tipMove} onMouseLeave={tipLeave}>
                        <div className="dc-vtop">{b.count}</div>
                        <div className="dc-vgrow"><div className="dc-vfill" style={{ height: `${(Number(b.count) || 0) / maxV * 96}px`, background: '#34C6F1' }} /></div>
                        <div className="dc-vlbl">{b.label}</div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
              <div className="mini-chart">
                <h4>Marca del activo (# unidades)</h4>
                <div className="dc-marca">
                  {(() => {
                    const maxM = Math.max(1, ...marcasDonut.map((m) => Number(m.count) || 0));
                    return marcasDonut.map((m) => (
                      <div className="dc-marca-row" key={m.key}
                        onMouseEnter={(e) => tipEnter(e, m.label, `${m.count} unidades`)}
                        onMouseMove={tipMove} onMouseLeave={tipLeave}>
                        <span className="dc-marca-lbl">{m.label}</span>
                        <div className="dc-marca-track"><div className="dc-marca-fill" style={{ width: `${(Number(m.count) || 0) / maxM * 100}%`, background: m.color }} /></div>
                        <span className="dc-marca-ct">{m.count}</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
              <div className="mini-chart">
                <h4>Residual/VF por plazo</h4>
                <div className="dc-vbars" style={{ height: 118 }}>
                  {(() => {
                    const rows = analitica.vr_plazo || [];
                    const maxV = Math.max(1, ...rows.map((v) => Number(v.vr_pct) || 0));
                    return rows.map((v) => (
                      <div className="dc-vcol" key={v.label}
                        onMouseEnter={(e) => tipEnter(e, `Residual/VF · ${v.label}`, [`${(Number(v.vr_pct) || 0).toFixed(1)}% VR/VF`, `${v.count} contratos`])}
                        onMouseMove={tipMove} onMouseLeave={tipLeave}>
                        <div className="dc-vtop">{(Number(v.vr_pct) || 0).toFixed(1)}%</div>
                        <div className="dc-vgrow"><div className="dc-vfill" style={{ height: `${(Number(v.vr_pct) || 0) / maxV * 96}px`, background: '#00BA6E' }} /></div>
                        <div className="dc-vlbl">{v.label} ({v.count})</div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      ) : null}

      {/* ══ Mapa de riesgo (ticket vs remanente) + concentración por cliente ══ */}
      <motion.div className="cart-row" {...reveal(2)}>
        <div className="card card-pad">
          <div className="card-h">
            <h3>Ticket vs plazo remanente — mapa de riesgo</h3>
            <span className="sub">AP · clic en un punto abre el contrato</span>
          </div>
          {scatterPts.length === 0 ? (
            <div className="empty-state"><i className="pi pi-map-marker" /><div className="es-title">Sin datos</div></div>
          ) : (
            <>
              <ScatterChart points={scatterPts} onPoint={(p) => onOpenCredito && onOpenCredito(p.id)}
                onHover={(p, e) => tipEnter(e, `${p.clave} · ${p.cliente || ''}`, [`Valor factura: ${money0(p.y)}`, `${p.x} meses al vencimiento`])}
                onMove={tipMove} onLeave={tipLeave} />
              <div className="chart-note">
                <span style={{ color: '#DC2626' }}>●</span> vence &lt;12m &nbsp;
                <span style={{ color: '#F0961E' }}>●</span> 12–24m &nbsp;
                <span style={{ color: '#34C6F1' }}>●</span> &gt;24m · tamaño = valor factura c/IVA.
                Puntos grandes cerca de la izquierda = tickets fuertes por renovar pronto.
              </div>
            </>
          )}
        </div>

        <div className="card card-pad">
          <div className="card-h">
            <h3>Concentración de cartera</h3>
            <span className="sub">top 5 clientes vs resto · por saldo</span>
            <div className="spacer" />
            <span className="tag">{concTop5Pct}% en top 5</span>
          </div>
          {concBars.length === 0 ? (
            <div className="empty-state"><i className="pi pi-users" /><div className="es-title">Sin datos</div></div>
          ) : (
            <HBars data={concBars} valueKey="saldo"
              onRow={(d, e) => openDrill(e, concCfg(d))}
              onRowHover={(d, e) => openDrill(e, concCfg(d))}
              onLeave={leaveTrigger} />
          )}
        </div>
      </motion.div>

      {/* ══ Rendimiento implícito por contrato + curva de extinción AP ══ */}
      <motion.div className="cart-row" {...reveal(2.1)}>
        <div className="card card-pad">
          <div className="card-h">
            <h3>Renta/VF por contrato — rendimiento implícito</h3>
            <span className="sub">renta mensual sobre valor factura · promedio {rentaVfProm}%</span>
          </div>
          {rentaVfBars.length === 0 ? (
            <div className="empty-state"><i className="pi pi-percentage" /><div className="es-title">Sin datos</div></div>
          ) : (
            <>
              <div className="scroll-y-slim">
                <HBars data={rentaVfBars} valueKey="pct" format={pctFmt}
                  onRowHover={(d, e) => tipEnter(e, d.label, [`Renta/VF: ${pctFmt(d.pct)}`, `Promedio: ${pctFmt(rentaVfProm)}`])}
                  onLeave={tipLeave} />
              </div>
              <div className="chart-note">
                <span style={{ color: '#00BA6E' }}>●</span> en o sobre el promedio ·{' '}
                <span style={{ color: '#F0961E' }}>●</span> debajo del promedio (rendimiento relativo menor).
              </div>
            </>
          )}
        </div>

        <div className="card card-pad">
          <div className="card-h">
            <h3>Curva de extinción — cartera AP</h3>
            <span className="sub">rentas por cobrar si no entra negocio nuevo · sin residuales</span>
          </div>
          {extPts.length === 0 ? (
            <div className="empty-state"><i className="pi pi-hourglass" /><div className="es-title">Sin rentas futuras</div></div>
          ) : (
            <>
              {(() => {
                const pts = extPts;
                const n = pts.length;
                const W = 600, H = 200, P = { l: 54, r: 12, t: 12, b: 26 };
                const maxY = Math.max(1, ...pts.map((p) => Number(p.saldo) || 0));
                const plotW = W - P.l - P.r, plotH = H - P.t - P.b;
                const xAt = (i) => P.l + (n > 1 ? (i / (n - 1)) * plotW : plotW / 2);
                const yAt = (v) => P.t + plotH - ((Number(v) || 0) / maxY) * plotH;
                const line = pts.map((p, i) => `${xAt(i).toFixed(1)},${yAt(p.saldo).toFixed(1)}`).join(' ');
                const area = `${P.l},${P.t + plotH} ${line} ${xAt(n - 1)},${P.t + plotH}`;
                const yTicks = [0, 0.5, 1].map((f) => f * maxY);
                return (
                  <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 180, display: 'block', marginTop: 8 }}>
                    {yTicks.map((t, k) => (
                      <g key={k}>
                        <line x1={P.l} x2={W - P.r} y1={yAt(t)} y2={yAt(t)} stroke="var(--line-soft)" />
                        <text x={P.l - 6} y={yAt(t) + 3} textAnchor="end" fontSize="9.5" fill="var(--ink-4)">{moneyM(t)}</text>
                      </g>
                    ))}
                    <polygon points={area} fill="#34C6F1" fillOpacity="0.08" />
                    <polyline points={line} fill="none" stroke="#34C6F1" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
                    {pts.map((p, i) => (
                      <circle key={i} cx={xAt(i)} cy={yAt(p.saldo)} r="4" fill="#fff" stroke="#34C6F1" strokeWidth="2.5"
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={(e) => tipEnter(e, p.label || p.mes, `${money0(p.saldo)} por cobrar`)}
                        onMouseMove={tipMove} onMouseLeave={tipLeave} />
                    ))}
                    {pts.map((p, i) => (p.label ? <text key={`x${i}`} x={xAt(i)} y={H - 8} textAnchor="middle" fontSize="9.5" fill="var(--ink-4)">{p.label}</text> : null))}
                  </svg>
                );
              })()}
              <div className="ext-stats">
                <div className="stat-tile">
                  <div className="slabel">Por cobrar hoy</div>
                  <div className="sval">{moneyM(extStats?.saldo_hoy)}</div>
                </div>
                <div className="stat-tile">
                  <div className="slabel">Cae a la mitad</div>
                  <div className="sval">{extStats?.mes_50 || '—'}</div>
                </div>
                <div className="stat-tile">
                  <div className="slabel">Se extingue</div>
                  <div className="sval">{extStats?.mes_fin || '—'}</div>
                </div>
              </div>
            </>
          )}
        </div>
      </motion.div>

      <motion.div className="cart-row" {...reveal(2)}>
        <div className="card card-pad">
          <div className="card-h">
            <h3>Cobranza proyectada</h3>
            <span className="sub">6 meses · por cobrar</span>
          </div>
          {missingCobranza ? (
            <HistoricalEmpty icon="pi pi-chart-bar" />
          ) : cobranzaRows.length === 0 ? (
            <div className="empty-state"><i className="pi pi-chart-bar" /><div className="es-title">Sin datos</div></div>
          ) : (
            <>
              <div className="dc-vbars" style={{ height: 172 }}>
                {(() => {
                  const maxC = Math.max(1, ...cobranzaRows.map((m) => Number(m.monto) || 0));
                  return cobranzaRows.map((m, i) => (
                    <div className="dc-vcol" key={m.mes || i}
                      onMouseEnter={(e) => tipEnter(e, `${m.label}${i === 0 ? ' · mes actual' : ''}`, `Por cobrar: ${money0(m.monto)}`)}
                      onMouseMove={tipMove} onMouseLeave={tipLeave}>
                      <div className="dc-vtop" style={{ color: i === 0 ? '#00875A' : '#0C2036' }}>{moneyM(m.monto)}</div>
                      <div className="dc-vgrow"><div className="dc-vfill" style={{ height: `${(Number(m.monto) || 0) / maxC * 128}px`, background: i === 0 ? '#00BA6E' : '#34C6F1', borderRadius: 7 }} /></div>
                      <div className="dc-vlbl" style={{ fontWeight: i === 0 ? 700 : 500, color: i === 0 ? '#00875A' : '#94A3B8' }}>{m.label}</div>
                    </div>
                  ));
                })()}
              </div>
              <div className="dc-evo-note" style={{ marginTop: 12 }}>Mes actual: lo que resta del periodo</div>
            </>
          )}
        </div>

        <div className="card card-pad">
          <div className="card-h">
            <h3>Saldo por etapa</h3>
            <span className="sub">pasa el cursor para ver contratos</span>
          </div>
          {missingEtapas ? (
            <HistoricalEmpty icon="pi pi-flag" />
          ) : etapaData.length === 0 ? (
            <div className="empty-state"><i className="pi pi-flag" /><div className="es-title">Sin atrasos</div><div className="es-sub">Ningún contrato en etapa de cobranza.</div></div>
          ) : (
            <HBars data={etapaData} valueKey="saldo"
              onRow={(d, e) => openDrill(e, etapaCfg(d.key))}
              onRowHover={(d, e) => openDrill(e, etapaCfg(d.key))}
              onLeave={leaveTrigger} />
          )}
        </div>
      </motion.div>

      <motion.div className="cart-row final-ops-row" {...reveal(2.1)}>
        <div className="card card-pad">
          <div className="card-h">
            <h3>Bandeja del día</h3>
            <span className="sub">vencidos y deteriorados · ordenados por días de atraso</span>
            <div className="spacer" />
            <span className="chip chip-judicial"><span className="pip" />{missingBandeja ? 'sin dato' : bandeja.length}</span>
          </div>
          {missingBandeja ? (
            <HistoricalEmpty icon="pi pi-inbox" />
          ) : bandeja.length === 0 ? (
            <div className="empty-state">
              <i className="pi pi-check-circle" />
              <div className="es-title">Nada vencido por hoy</div>
              <div className="es-sub">Todos los contratos están al corriente.</div>
            </div>
          ) : (
            <div className="scroll-y">
              <table className="tbl">
                <tbody>
                  {bandeja.map((b) => (
                    <tr key={b.id} onClick={() => onOpenCredito && onOpenCredito(b.id)}
                        style={{ cursor: 'pointer' }} title="Abrir detalle del contrato">
                      <td>
                        <div className="cust-cell">
                          <div className="ava-sm" style={{ background: avaColor(b.cliente || b.clave_contrato) }}>{iniciales(b.cliente)}</div>
                          <div>
                            <div className="nm">{b.cliente || '—'}</div>
                            <div className="sub">{b.clave_contrato} · {b.dias_vencido} días</div>
                          </div>
                        </div>
                      </td>
                      <td className="right">
                        <span className="money neg">{money(b.mora_acumulada)}</span>
                        <div className="t-xs muted">saldo {money0(b.saldo_insoluto)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card card-pad">
          <div className="card-h">
            <h3>Próximos vencimientos</h3>
            <span className="sub">31 días · agrupados por fecha</span>
            <div className="spacer" />
            <span className="chip chip-preventiva"><span className="pip" />{missingProximos ? 'sin dato' : `${proximosRows.length} cobros`}</span>
          </div>
          {missingProximos ? (
            <HistoricalEmpty icon="pi pi-calendar" />
          ) : proximosGroups.length === 0 ? (
            <div className="empty-state">
              <i className="pi pi-calendar" />
              <div className="es-title">Sin vencimientos próximos</div>
              <div className="es-sub">Ningún contrato tiene cobro programado en los próximos 31 días.</div>
            </div>
          ) : (
            <div className="scroll-y prox-list">
              {proximosGroups.map((g) => (
                <div className="prox-group" key={g.fecha}>
                  <div className="prox-date">
                    <span>{fmtFecha(g.fecha)}</span>
                    <span className="muted">{plural(g.items.length, 'cobro', 'cobros')} · {money0(g.total)}</span>
                  </div>
                  {g.items.map((p, i) => (
                    <button type="button" className="prox-row" key={`${p.credito_id}-${p.tipo}-${p.numero}-${i}`}
                      onClick={() => onOpenCredito && onOpenCredito(p.credito_id)}>
                      <span className="drill-row-main">
                        <span className="nm">{p.cliente || '—'}</span>
                        <span className="sub">{p.clave_contrato} · {p.tipo === 'renta' ? 'renta' : 'parcialidad'} {p.numero}</span>
                      </span>
                      <span className="money">{money(p.monto)}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      <DrillPopover open={drill.open} onClose={closeDrill} anchorRect={drill.rect}
        title={drill.title} subtitle={drill.subtitle} accent={drill.accent}
        rows={drill.rows || []} emptyText={drill.emptyText}
        onPointerStay={stayPopover} onPointerLeave={scheduleClose}
        onRowClick={(id) => onOpenCredito && onOpenCredito(id)} />

      {chartTip ? (
        <div className="dc-charttip" style={{ left: chartTip.x, top: chartTip.y }}>
          <div className="dc-charttip-t">{chartTip.title}</div>
          {chartTip.lines.map((l, i) => <div className="dc-charttip-l" key={i}>{l}</div>)}
        </div>
      ) : null}
    </>
  );
};

export default CarteraDashboard;
